import { apiClient } from './client';

export interface BonusRule {
  id: string;
  name: string;
  bonusPercent: number;
  minDeposit: string;
  maxBonus: string | null;
  wagerMultiplier: string;
  isActive: boolean;
  schedule: {
    dayOfWeek: number[];
    startHour: string;
    endHour: string;
    priority: number;
  } | null;
  audience: Record<string, unknown> | null;
  createdAt: string;
}

export interface BonusScheduleState {
  mode: 'manual' | 'auto';
  defaultPercent: number;
  currentDay: string;
  currentHour: number;
  activeRules: BonusRule[];
}

export interface CreateBonusRuleDto {
  name: string;
  bonusPercent: number;
  minDeposit?: number;
  maxBonus?: number;
  wagerMultiplier?: number;
  isActive?: boolean;
  schedule?: {
    dayOfWeek: number[];
    startHour: string;
    endHour: string;
    priority: number;
  };
  audience?: {
    all?: boolean;
    clientIds?: string[];
    tags?: string[];
    vipOnly?: boolean;
    minLifetimeDeposits?: number;
  };
}

export interface UpdateBonusRuleDto extends Partial<CreateBonusRuleDto> {}

export const bonusApi = {
  list: async (): Promise<BonusRule[]> => {
    const { data } = await apiClient.get('/admin/bonus');
    return data;
  },
  create: async (dto: CreateBonusRuleDto): Promise<BonusRule> => {
    const { data } = await apiClient.post('/admin/bonus', dto);
    return data;
  },
  update: async (id: string, dto: UpdateBonusRuleDto): Promise<BonusRule> => {
    const { data } = await apiClient.patch(`/admin/bonus/${id}`, dto);
    return data;
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/bonus/${id}`);
  },
  getSchedule: async (): Promise<BonusScheduleState> => {
    const { data } = await apiClient.get('/admin/bonus/schedule');
    return data;
  },
  setMode: async (mode: 'manual' | 'auto', defaultPercent?: number): Promise<BonusScheduleState> => {
    const { data } = await apiClient.post('/admin/bonus/schedule/mode', { mode, defaultPercent });
    return data;
  },
  applyNow: async (ruleId: string, clientId: string, depositAmount: number): Promise<{ bonusAmount: number; wagerRequired: number }> => {
    const { data } = await apiClient.post('/admin/bonus/apply-now', { ruleId, clientId, depositAmount });
    return data;
  },
  forceBonus: async (percent: number): Promise<{ mode: string; defaultPercent: number }> => {
    const { data } = await apiClient.post('/admin/bonus/force', { percent });
    return data;
  },
};
